import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { TASK_IDS } from "@/shared/constants/taskIds";

interface ProbateAnswers {
  hasWill: string | null;
  hasTrust: string | null;
  probateState: string | null;
  probateCounty: string | null;
}

export function useProbateAnswers(
  estateId: number | undefined,
): ProbateAnswers & { isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ["probateAnswers", estateId],
    queryFn: async () => {
      const [openProbate, willTask, authorityTask] = await Promise.all([
        supabase
          .from("estate_tasks")
          .select("answers")
          .eq("estate_id", estateId!)
          .eq("task_id", "open_probate")
          .maybeSingle(),
        supabase
          .from("estate_tasks")
          .select("answers")
          .eq("estate_id", estateId!)
          .eq("task_id", TASK_IDS.WILL)
          .maybeSingle(),
        supabase
          .from("estate_tasks")
          .select("answers")
          .eq("estate_id", estateId!)
          .eq("task_id", TASK_IDS.LOT)
          .maybeSingle(),
      ]);

      if (openProbate.error) throw openProbate.error;
      if (willTask.error) throw willTask.error;
      if (authorityTask.error) throw authorityTask.error;

      const probateData = openProbate.data?.answers as Record<string, string> | null;
      const willData = willTask.data?.answers as Record<string, string> | null;
      const authorityData = authorityTask.data?.answers as Record<string, string> | null;

      return {
        hasWill: probateData?.["has-will"] ?? willData?.["has-will"] ?? null,
        hasTrust: authorityData?.trust ?? null,
        probateState: probateData?.probate_state ?? null,
        probateCounty: probateData?.probate_county ?? null,
      };
    },
    enabled: !!estateId,
  });

  return {
    ...(data ?? { hasWill: null, hasTrust: null, probateState: null, probateCounty: null }),
    isLoading,
  };
}

export function useWillAnswer(estateId: number | undefined) {
  const { hasWill } = useProbateAnswers(estateId);
  return hasWill;
}
